import { useState } from 'react';
import { Box, IconButton, makeStyles } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { SBase, SFormikTextfield, SText } from '@setvi/shared/components';
import { PREDEFINED_LABELS } from '../edit-option-details-dialog';

const useStyles = makeStyles(() => ({
  content: {
    width: '100%',
    height: '100%',
    padding: '24px',
    backgroundColor: '#F8F9FA'
  },
  sectionHeading: {
    fontSize: '20px',
    fontWeight: 600,
    color: '#20262D',
    marginBottom: '12px'
  },
  sectionDescription: {
    fontSize: '14px',
    color: '#333D47',
    marginBottom: '20px'
  },
  labelsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px'
  },
  labelPill: {
    padding: '8px 12px',
    borderRadius: '120px',
    backgroundColor: '#F8F9FA',
    border: '1px solid #AFBAC6',
    cursor: 'pointer',
    fontSize: '14px',
    color: '#5D7083',
    width: 'fit-content',
    textAlign: 'center',
    transition: 'all 0.1s ease',
    '&:hover': {
      borderColor: '#1E96FC'
    }
  },
  labelPillSelected: {
    borderColor: '#1E96FC',
    boxShadow: 'inset 0 0 0 1px #1E96FC',
    fontWeight: 500
  },
  customLabelSection: {
    marginTop: '20px',
    maxWidth: '180px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  }
}));

interface EditLabelDialogProps {
  itemId: string;
  labelType?: string;
  customLabel?: string;
  onClose: () => void;
  onSave: (
    itemId: string,
    data: {
      labelType: string;
      customLabel?: string;
    }
  ) => void;
}

const validationSchema = Yup.object({
  customLabel: Yup.string().max(11, 'Max 11 characters')
});

export const EditLabelDialog = ({
  itemId,
  labelType = null,
  customLabel: initialCustomLabel = '',
  onClose,
  onSave
}: EditLabelDialogProps) => {
  const classes = useStyles();
  const [selectedLabel, setSelectedLabel] = useState(
    PREDEFINED_LABELS.includes(labelType) ? labelType : ''
  );

  const isEditing = !!labelType;
  const actionLabel = isEditing ? 'Edit' : 'Add';

  const customLabelValue = PREDEFINED_LABELS.includes(labelType)
    ? initialCustomLabel
    : labelType;

  return (
    <Formik
      initialValues={{
        customLabel: customLabelValue
      }}
      validationSchema={validationSchema}
      onSubmit={values => {
        onSave(itemId, {
          labelType: selectedLabel,
          customLabel: selectedLabel === 'custom' ? values?.customLabel : ''
        });
        onClose();
      }}>
      {formik => (
        <Form>
          <SBase
            style={{
              background: '#fff'
            }}
            container={{
              style: {
                overflow: 'hidden'
              }
            }}
            disablePadding
            header={{ title: `${actionLabel} Label Details`, onClose }}
            footer={{
              onConfirm: () => formik.handleSubmit(),
              onClose,
              confirmLabel: 'Save Changes',
              closeLabel: 'Cancel',
              disableConfirm:
                selectedLabel === 'custom' && !formik.values.customLabel?.trim()
            }}
            height="500px"
            width="600px">
            <Box className={classes.content}>
              <SText
                size="lg"
                weight="semibold"
                className={classes.sectionHeading}>
                Label Details
              </SText>
              <SText size="sm" className={classes.sectionDescription}>
                Choose a predefined label or create a custom one for this item.
              </SText>
              <Box className={classes.labelsContainer}>
                {PREDEFINED_LABELS.map(label => (
                  <Box
                    key={label}
                    className={`${classes.labelPill} ${selectedLabel === label ? classes.labelPillSelected : ''}`}
                    onClick={() => {
                      // Allow unselecting by clicking on already selected label
                      if (selectedLabel === label) {
                        setSelectedLabel('');
                        formik.setFieldValue('customLabel', '');
                      } else {
                        setSelectedLabel(label);
                        if (label !== 'custom') {
                          formik.setFieldValue('customLabel', '');
                        }
                      }
                    }}>
                    {label.charAt(0).toUpperCase() + label.slice(1)}
                  </Box>
                ))}
              </Box>

              {selectedLabel === 'custom' && (
                <Box className={classes.customLabelSection}>
                  <SFormikTextfield
                    name="customLabel"
                    label="Custom Label"
                    placeholder="Enter Label"
                    maxLength={11}
                    disableErrorMessage
                    InputProps={{
                      style: { backgroundColor: '#FFFFFF', height: 44 }
                    }}
                    endIcon={
                      formik.values.customLabel ? (
                        <IconButton
                          size="small"
                          onClick={() =>
                            formik.setFieldValue('customLabel', '')
                          }>
                          <CloseIcon
                            style={{
                              fontSize: 16,
                              color: '#5D7083',
                              marginRight: -12
                            }}
                          />
                        </IconButton>
                      ) : undefined
                    }
                  />
                  <SText
                    size="xs"
                    title="(max 11 characters)"
                    fontColor="#333D47"
                    style={{
                      marginLeft: 2
                    }}
                  />
                </Box>
              )}
            </Box>
          </SBase>
        </Form>
      )}
    </Formik>
  );
};
